/**
 * Branded ids and the deterministic helpers that mint them.
 *
 * Every id is a content hash of what it identifies, so a rerun over the same
 * export yields the same ids in the CLI and in the browser. Goldens (M2.10)
 * diff by id; corrections (M4.2) reference claims by id across sessions.
 */
import { sha256Hex } from './hash.js'

declare const brand: unique symbol
export type Brand<T, B extends string> = T & { readonly [brand]: B }

export type SourceId = Brand<string, 'SourceId'>
export type EvidenceId = Brand<string, 'EvidenceId'>
export type ClaimId = Brand<string, 'ClaimId'>
export type LedgerItemId = Brand<string, 'LedgerItemId'>
export type LineageGroupId = Brand<string, 'LineageGroupId'>
export type PersonId = Brand<string, 'PersonId'>
export type OrganizationId = Brand<string, 'OrganizationId'>
export type CorrectionId = Brand<string, 'CorrectionId'>
export type SuppressionId = Brand<string, 'SuppressionId'>
export type SignalId = Brand<string, 'SignalId'>

/** Length-prefixes each part so `['ab', 'c']` and `['a', 'bc']` never collide. */
export function deterministicId(prefix: string, parts: readonly string[]): string {
  const material = parts.map((p) => `${p.length}:${p}`).join('|')
  return `${prefix}_${sha256Hex(material).slice(0, 24)}`
}

export const sourceId = (provider: string, key: string): SourceId =>
  deterministicId('src', [provider, key]) as SourceId

export const evidenceId = (source_id: SourceId, start: number, end: number): EvidenceId =>
  deterministicId('ev', [source_id, String(start), String(end)]) as EvidenceId

export const claimId = (
  evidence_ids: readonly EvidenceId[],
  claim_type: string,
  proposition_key: string,
): ClaimId =>
  deterministicId('cl', [...[...evidence_ids].sort(), claim_type, proposition_key]) as ClaimId

export const ledgerItemId = (claim_id: ClaimId): LedgerItemId =>
  deterministicId('it', [claim_id]) as LedgerItemId

export const lineageGroupId = (claim_ids: readonly ClaimId[]): LineageGroupId =>
  deterministicId('lg', [...claim_ids].sort()) as LineageGroupId

/** Canonical address: lower-cased, trimmed. Aliases merge into one person elsewhere. */
export const personId = (canonical_address: string): PersonId =>
  deterministicId('p', [canonical_address.trim().toLowerCase()]) as PersonId

export const organizationId = (domain: string): OrganizationId =>
  deterministicId('org', [domain.trim().toLowerCase()]) as OrganizationId

export const signalId = (kind: string, item_id: LedgerItemId, key: string): SignalId =>
  deterministicId('sig', [kind, item_id, key]) as SignalId

export const correctionId = (
  time: string,
  affects: readonly string[],
  field: string,
): CorrectionId => deterministicId('cor', [time, ...affects, field]) as CorrectionId

export const suppressionId = (target: string, reason: string, time: string): SuppressionId =>
  deterministicId('sup', [target, reason, time]) as SuppressionId
